import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, mergeMap, take } from 'rxjs/operators';
import { MemberService } from './member.service';
import { TabPermissionService } from 'app/core/auth/tab-permission.service';
import { MembersType } from '../../../modules/admin/financial/entries/list/tabs/entries/dialogs/entry/types/members.type';

/**
 * Avatar de membro exibido no card da igreja (sidebar).
 */
export interface MemberAvatar
{
    id: number;
    avatar: string;
    fullName: string;
    /** Fallback visual ativado quando a foto real falha ao carregar (onerror). */
    failed?: boolean;
}

/**
 * Chaves da CADEIA de permissão do endpoint GET /secretary/membership/membership.
 *
 * Gate E1 (reserva architect 2026-09-18 — F1, aplicado com evidência): um
 * consumidor GLOBAL (shell) de endpoint gateado por ABA precisa de gate
 * DETERMINÍSTICO (estados full|hidden desde 2026-08-12) no nível do ENDPOINT.
 * O CheckScreenPermission itera a hierarquia da rota (módulo → aba) e NEGA se
 * QUALQUER elo for hidden — sem X-Access-Context não há liberação por contexto
 * (o shell não declara header). Verificado empiricamente (2026-09-18, iebrd):
 * perfil treasury com mem-members-list=full mas mem-access=hidden recebe 403
 * ACCESS_DENIED no endpoint apesar do flat apontar a aba full. Logo o gate
 * correto é a CONJUNÇÃO das duas chaves (módulo E aba full) — espelha a
 * decisão do middleware e elimina os paths residuais de 403 → /home
 * (AuthInterceptor). NÃO usa OR de módulo/concessão de contexto
 * (isContextGranted — inútil sem header, ponto do architect).
 */
const MEMBERSHIP_MODULE_KEY = 'mem-access';
const MEMBER_LIST_SCREEN_KEY = 'mem-members-list';

/**
 * Envelope de página do endpoint (simplePaginate) — shape real validado via
 * diagnóstico read-only: { members, links.next, meta, total, countRows }.
 */
interface MembershipPageResponse
{
    members: MembersType[];
    links?: { next?: string | null } | null;
    meta?: unknown;
    total?: number;
    countRows?: number;
}

/**
 * Estado dos avatares reais dos membros no card da igreja (sidebar).
 *
 * Segue o padrão canônico "snapshot único por sessão" do StorageUsageService:
 *  - fetch IDEMPOTENTE — `load()` no-op após a 1ª resolução (sucesso OU erro);
 *  - `reset()` por login/logout (AuthService.signIn/signOut) com **epoch guard**
 *    (reserva architect 2026-09-18 — F3): respostas em voo de uma sessão
 *    ANTERIOR são descartadas — nunca repopulam o cache do usuário novo;
 *  - cache em BehaviorSubject — o card (shell `classic`, recriado entre blocos
 *    de rota) LÊ o cache; navegações nunca re-chamam o endpoint;
 *  - **loading NASCE `true`** (regressão flash corrigida 2026-09-18): o card
 *    já renderiza o skeleton no PRIMEIRO ciclo de CD — a sequência de render é
 *    SEMPRE skeleton → estado final (fotos reais OU placeholders), NUNCA
 *    placeholders estáticos antes do skeleton (antes, `_loading$` iniciava
 *    `false` e o skeleton só ligava DEPOIS do gate assíncrono de permissões);
 *    TODO caminho terminal emite `loading=false` (gate negado, fetch com
 *    sucesso/erro/403 via finalize) e o `reset()` (nova sessão) volta
 *    `loading=true` — nenhum path deixa o skeleton preso;
 *  - GATE E1 determinístico: cadeia do endpoint FULL (`isFull('mem-access')` E
 *    `isFull('mem-members-list')` — espelha o CheckScreenPermission, que nega
 *    quando qualquer elo é hidden) — sem a cadeia full → fallback silencioso
 *    (placeholders), SEM chamada — nunca um 403 ACCESS_DENIED chega ao
 *    AuthInterceptor (não há expulsão /home);
 *  - erro/403 residual de rede → cacheado como lista vazia (fallback silencioso).
 *
 * Coleta: página 1 (simplePaginate, ?page=1 → 30 itens) e, se necessário,
 * páginas seguintes até completar 4 membros com avatar (teto de ~3 páginas).
 */
@Injectable({
    providedIn: 'root'
})
export class MemberAvatarsService
{
    /** Número máximo de avatares exibidos no card. */
    private static readonly MAX_AVATARS = 4;
    /** Teto seguro de páginas varridas na coleta paginada. */
    private static readonly MAX_PAGES = 3;

    private _avatars$: BehaviorSubject<MemberAvatar[]> = new BehaviorSubject<MemberAvatar[]>([]);
    /** Flag de carregamento — NASCE `true` para que o card mostre o skeleton já
     *  no primeiro ciclo de change detection, ANTES de qualquer fetch/avaliação
     *  de permissão (regressão do flash corrigida 2026-09-18: antes nascia
     *  `false` e o template renderizava os placeholders estáticos até o gate
     *  assíncrono ligar o loading). Todo caminho terminal emite `false`. */
    private _loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private _loaded: boolean = false;
    private _fetching: boolean = false;
    /** Época do cache: incrementada em reset() — respostas HTTP em voo de uma
     *  época anterior (ex.: sessão A) são DESCARTADAS ao completar depois do
     *  reset (nunca repopulam o cache com dados do usuário anterior). */
    private _epoch = 0;

    /**
     * Constructor
     */
    constructor(
        private _memberService: MemberService,
        private _tabPermission: TabPermissionService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Avatares atuais (vazio enquanto não carregado ou após erro/sem permissão).
     */
    get avatars(): MemberAvatar[]
    {
        return this._avatars$.value;
    }

    /**
     * Observable dos avatares — emite o último valor conhecido do cache.
     */
    get avatars$(): Observable<MemberAvatar[]>
    {
        return this._avatars$.asObservable();
    }

    /**
     * Observable da flag de carregamento (skeleton). Nasce `true` (estado
     * inicial = carregando — o card já renderiza o skeleton no 1º ciclo) e
     * emite `false` em TODO caminho terminal (finalize do fetch, gate negado,
     * erro/403).
     */
    get loading$(): Observable<boolean>
    {
        return this._loading$.asObservable();
    }

    /**
     * True quando a busca única da sessão terminou (sucesso, erro OU
     * avaliação de permissão negada).
     */
    get loaded(): boolean
    {
        return this._loaded;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Dispara a busca ÚNICA dos avatares por sessão de aplicação.
     *
     * Idempotente: chamadas repetidas (login, layout/shell recriado, guard)
     * NÃO geram novas requests — apenas a 1ª por sessão busca o endpoint.
     *
     * Sequência (sem race):
     *   1. Aguarda loadPermissions() — flat de permissões revalidado no
     *      servidor (fresco, sem herança de sessão anterior);
     *   2. Descarta a execução se a época mudou (reset/login trocado em voo);
     *   3. Avalia o gate E1 (cadeia do endpoint full: `mem-access` E
     *      `mem-members-list`) → sem a cadeia full resolve vazio SEM disparar
     *      HTTP;
     *   4. Com a aba full → coleta paginada (teto 4 avatares / 3 páginas).
     */
    load(): void
    {
        // Já terminou (sucesso, erro ou permissão negada) ou está em voo → não re-busca.
        if ( this._loaded || this._fetching )
        {
            return;
        }

        const epoch = this._epoch;
        this._fetching = true;

        this._tabPermission.loadPermissions()
            .pipe(
                take(1),
                mergeMap(() =>
                {
                    // Época antiga (reset()/login trocado durante o voo):
                    // interrompe a coleta — a resposta seria descartada mesmo assim.
                    if ( epoch !== this._epoch )
                    {
                        return of([]);
                    }

                    if ( !this._canLoadEndpoint() )
                    {
                        // Sem a ABA full (gate E1 determinístico) → fallback
                        // silencioso (placeholders atuais), SEM chamada ao
                        // endpoint — nada chega ao AuthInterceptor (sem /home).
                        return of([]);
                    }

                    this._loading$.next(true);
                    return this._collectAvatars(1, []);
                }),
                catchError(() => of([])),
                finalize(() =>
                {
                    // Resposta de época antiga: o reset() já zelou pelo estado
                    // (_loaded/_fetching/_loading$/cache) — não sobrescrever.
                    if ( epoch !== this._epoch )
                    {
                        return;
                    }

                    this._fetching = false;
                    this._loaded = true;
                    this._loading$.next(false);
                })
            )
            .subscribe((avatars: MemberAvatar[]) =>
            {
                // Epoch guard: resposta de época anterior é DESCARTADA — nunca
                // repopula o cache com dados do usuário/sessão anterior.
                if ( epoch !== this._epoch )
                {
                    return;
                }

                this._avatars$.next(avatars);
            });
    }

    /**
     * Nova sessão (login/logout): incrementa a época (invalida qualquer coleta
     * em voo), limpa o cache e permite nova busca no próximo load() — cada
     * login traz os avatares frescos do tenant. Loading volta a `true`: a
     * próxima sessão também nasce com skeleton (nunca placeholders estáticos
     * no estado inicial — regressão flash corrigida 2026-09-18).
     */
    reset(): void
    {
        this._epoch++;
        this._loaded = false;
        this._fetching = false;
        this._avatars$.next([]);
        this._loading$.next(true);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Gate E1 determinístico no nível do ENDPOINT (F1 — reserva architect
     * 2026-09-18, aplicado com evidência): o consumidor é GLOBAL (shell, sem
     * X-Access-Context), então a única garantia de 200 é o acesso DIRETO pela
     * CADEIA inteira do endpoint — módulo E aba ambos full (o middleware nega
     * quando qualquer elo é hidden; verificado: treasury aba full + módulo
     * hidden → 403). Sem a cadeia full → NÃO dispara (placeholder estático,
     * silencioso — nada chega ao AuthInterceptor, sem expulsão /home).
     */
    private _canLoadEndpoint(): boolean
    {
        return this._tabPermission.isFull(MEMBERSHIP_MODULE_KEY)
            && this._tabPermission.isFull(MEMBER_LIST_SCREEN_KEY);
    }

    /**
     * Coleta paginada dos avatares: página `page` do endpoint de membresia,
     * extrai os membros com avatar e continua nas próximas páginas enquanto
     * não completar o teto (4) e houver `links.next` (teto de páginas: 3).
     */
    private _collectAvatars(page: number, acc: MemberAvatar[]): Observable<MemberAvatar[]>
    {
        if ( acc.length >= MemberAvatarsService.MAX_AVATARS || page > MemberAvatarsService.MAX_PAGES )
        {
            return of(acc.slice(0, MemberAvatarsService.MAX_AVATARS));
        }

        return this._memberService.getMembers(page)
            .pipe(
                take(1),
                catchError(() => of(null)),
                mergeMap((response: any) =>
                {
                    const pageAvatars = this._extractAvatars(response as MembershipPageResponse | null);
                    const merged = [...acc, ...pageAvatars];

                    const hasNext = !!(response && response.links && response.links.next);
                    if ( merged.length < MemberAvatarsService.MAX_AVATARS
                        && hasNext
                        && page < MemberAvatarsService.MAX_PAGES )
                    {
                        return this._collectAvatars(page + 1, merged);
                    }

                    return of(merged.slice(0, MemberAvatarsService.MAX_AVATARS));
                })
            );
    }

    /**
     * Extrai os membros com avatar não-nulo e não-vazio do envelope real do
     * endpoint (tipado contra o MembersType existente — reserva F5):
     * `personDataAndIdentification.avatar` = URL absoluta (S3) ou null.
     */
    private _extractAvatars(response: MembershipPageResponse | null): MemberAvatar[]
    {
        if ( !response || !Array.isArray(response.members) )
        {
            return [];
        }

        return response.members
            .filter((member: MembersType) => !!member && !!member.personDataAndIdentification)
            .map((member: MembersType): MemberAvatar => ({
                id: member.id,
                avatar: member.personDataAndIdentification.avatar,
                fullName: member.personDataAndIdentification.fullName || ''
            }))
            .filter((item: MemberAvatar) => typeof item.avatar === 'string' && item.avatar.trim() !== '');
    }
}